// src/pages/useAltScopedPageData.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiGet } from "../api/api";
import { useData } from "./Cache";
import { ensureRequestedScope, scopeOptionLabel } from "../utils/archiveScope";
import { usePortal } from "../portal/PortalContext";
import { findPortalByLeague, findPortalLeague, portalPath } from "../portal/portalPaths";

const safeArr = (value) => (Array.isArray(value) ? value : []);

const toIdString = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const comparePriority = (a, b) => {
  const aVal = a ?? null;
  const bVal = b ?? null;
  if (aVal === null && bVal === null) return 0;
  if (aVal === null) return 1;
  if (bVal === null) return -1;
  return Number(aVal) - Number(bVal);
};

const pickDefaultLeagueId = (leagues) => {
  const list = safeArr(leagues).slice();
  list.sort((a, b) => {
    const pri = comparePriority(a?.league_priority, b?.league_priority);
    if (pri !== 0) return pri;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
  return toIdString(list[0]?.id);
};

const pickDefaultSeasonId = (seasons) => {
  const list = safeArr(seasons).slice();
  list.sort((a, b) => {
    const pri = comparePriority(a?.season_priority, b?.season_priority);
    if (pri !== 0) return pri;
    return Number(b?.id || 0) - Number(a?.id || 0);
  });
  return toIdString(list[0]?.id);
};

/**
 * Sitenin il / lig / sezon kapsamı (üstteki filtreler).
 *
 * Varsayılan: kapsam adresin yol parametrelerinden okunur ve sayfa
 * /sehir/:il/lig/:lig/sezon/:sezon/<pageKey> adresine yönlendirilir.
 * Farklı adres yapısı olan sayfalar (cezalar, iletişim, haberler) için:
 *   pathFor(city, league, season) — sayfanın kendi adresi (lig/sezon boş
 *                                   gelebilir; seçim sonra tamamlanır)
 *   scopeParams                  — kapsam yol yerine sorgudan okunuyorsa
 *                                   { cityId, leagueId, seasonId }
 *   defaultCityId                — adreste il yokken seçilecek il (ör. hesabın ili)
 *   prefetch                     — false: kapsamın maç/puan verisi önden çekilmez
 *
 * Özel lig sayfası (Junior Cup, bkz. src/portal): adres /<slug>/... ise lig
 * listesi yalnız o sayfaya atanmış liglerden, sezonlar o ligin tüm
 * sezonlarından oluşur; il ligden gelir. Atanmış bir ligin eski
 * /sehir/... adresi açılırsa sayfa portal adresine yönlenir.
 */
export function useAltScopedPageData({ pageKey, pathFor = null, scopeParams = null, defaultCityId = "", prefetch = true } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchScope, refetchMeta, cities: cachedCities = [] } = useData();

  const routeParams = useParams();
  const { cityId: cityParam, leagueId: leagueParam, seasonId: seasonParam } = scopeParams || routeParams;

  const { portals, ready: portalsReady, portal: activePortal, isPortalRoute } = usePortal();
  // Kendi adres yapısı olan sayfalar (pathFor) portal adresine taşınmaz.
  const portal = !pathFor && isPortalRoute ? activePortal : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [cityOptions, setCityOptions] = useState([]);
  const [leagueOptions, setLeagueOptions] = useState([]);
  const [seasonOptions, setSeasonOptions] = useState([]);

  const [selectedCity, setSelectedCity] = useState("");
  const [selectedLeague, setSelectedLeague] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");

  const requestRef = useRef(0);
  const page = pageKey || "anasayfa";

  const hasScopedSelection = Boolean(selectedCity && selectedLeague && selectedSeason);

  const scopeLabel = useMemo(() => {
    if (!hasScopedSelection) return "";
    const cityLabel = cityOptions.find((o) => o.id === selectedCity)?.label || selectedCity;
    const leagueLabel = leagueOptions.find((o) => o.id === selectedLeague)?.label || selectedLeague;
    const seasonLabel = seasonOptions.find((o) => o.id === selectedSeason)?.label || selectedSeason;
    return `${cityLabel} • ${leagueLabel} • ${seasonLabel}`;
  }, [cityOptions, leagueOptions, seasonOptions, selectedCity, selectedLeague, selectedSeason, hasScopedSelection]);

  const buildScopedPath = useCallback(
    (nextCity, nextLeague, nextSeason, nextPage = page) => {
      if (!nextCity || !nextLeague || !nextSeason) return "";
      return `/sehir/${encodeURIComponent(nextCity)}/lig/${encodeURIComponent(nextLeague)}/sezon/${encodeURIComponent(
        nextSeason
      )}/${encodeURIComponent(nextPage)}`;
    },
    [page]
  );

  const targetFor = useCallback(
    (nextCity, nextLeague, nextSeason) => {
      if (pathFor) return pathFor(nextCity, nextLeague, nextSeason);
      // Lig bir özel sayfaya atanmışsa (Junior Cup) kısa adresi kullanılır.
      const leaguePortal = findPortalByLeague(portals, nextLeague);
      if (leaguePortal && nextLeague && nextSeason) return portalPath(leaguePortal, nextLeague, nextSeason, page);
      return buildScopedPath(nextCity, nextLeague, nextSeason, page);
    },
    [pathFor, buildScopedPath, page, portals]
  );

  /** Aynı kapsamda başka bir liste sayfasının adresi (maclar, puan-durumu…). */
  const pagePath = useCallback(
    (nextPage) => {
      if (!selectedCity || !selectedLeague || !selectedSeason) return "";
      const leaguePortal = findPortalByLeague(portals, selectedLeague);
      if (leaguePortal && ["anasayfa", "maclar", "puan-durumu", "oyuncular"].includes(nextPage)) {
        return portalPath(leaguePortal, selectedLeague, selectedSeason, nextPage);
      }
      return buildScopedPath(selectedCity, selectedLeague, selectedSeason, nextPage);
    },
    [portals, selectedCity, selectedLeague, selectedSeason, buildScopedPath]
  );

  useEffect(() => {
    if (!cachedCities.length) refetchMeta?.();
  }, [cachedCities.length, refetchMeta]);

  useEffect(() => {
    const run = async () => {
      if (!cachedCities.length || !portalsReady) return;

      const requestId = ++requestRef.current;

      // Özel sayfaya atanmış ligin eski /sehir/... adresi: istenen sezonla
      // doğrudan portal adresine geçilir (sezon orada doğrulanır).
      if (!pathFor && !portal && activePortal && leagueParam && findPortalByLeague([activePortal], leagueParam)) {
        const target = portalPath(activePortal, leagueParam, seasonParam, page);
        if (target && location.pathname !== target) {
          navigate(target, { replace: true });
          return;
        }
      }

      if (portal) {
        // Özel lig sayfası: ligler ve sezonlar portal kaydından gelir.
        const leagueOpts = safeArr(portal.leagues).map((league) => ({
          id: toIdString(league.id),
          label: String(league.label || "").trim(),
          raw: league,
        }));
        const finalLeague =
          leagueOpts.find((l) => l.id === toIdString(leagueParam))?.id ||
          toIdString(portal.defaultLeagueId) ||
          leagueOpts[0]?.id ||
          "";
        const league = findPortalLeague(portal, finalLeague);
        const finalCity = toIdString(league?.cityId);
        const cityLabel =
          safeArr(cachedCities).find((city) => toIdString(city?.id) === finalCity)?.label || finalCity;
        const seasonOpts = safeArr(league?.seasons).map((season) => ({
          id: toIdString(season.id),
          label: scopeOptionLabel({ ...season, is_archived: season.isArchived }),
          raw: season,
        }));
        const fallbackSeason =
          finalLeague === toIdString(portal.defaultLeagueId) ? portal.defaultSeasonId : league?.defaultSeasonId;
        const finalSeason =
          seasonOpts.find((s) => s.id === toIdString(seasonParam))?.id || toIdString(fallbackSeason) || seasonOpts[0]?.id || "";

        setCityOptions(finalCity ? [{ id: finalCity, label: String(cityLabel || "").trim() }] : []);
        setLeagueOptions(leagueOpts);
        setSeasonOptions(seasonOpts);
        setSelectedCity(finalCity);
        setSelectedLeague(finalLeague);
        setSelectedSeason(finalSeason);
        setError(finalLeague && finalSeason ? "" : "Bu sayfaya henüz lig ya da sezon atanmamış.");
        setLoading(false);

        const target = portalPath(portal, finalLeague, finalSeason, page);
        if (finalLeague && finalSeason && target && location.pathname !== target) navigate(target, { replace: true });
        return;
      }

      // Lig/sezonu olmayan ilde de kendi adres yapısı olan sayfanın adresi
      // seçilen ili taşısın (ör. /haberler?city=).
      const syncPartialPath = (city, league, season) => {
        if (!pathFor || !city) return;
        const target = pathFor(city, league, season);
        if (target && `${location.pathname}${location.search}` !== target) navigate(target, { replace: true });
      };
      setLoading(true);
      setError("");

      try {
        const cities = safeArr(cachedCities).map((city) => ({
          id: toIdString(city?.id),
          label: String(city?.label || "").trim(),
        }));
        setCityOptions(cities);

        const requestedCity = toIdString(cityParam);
        const preferredCity = toIdString(defaultCityId);
        const finalCity =
          cities.find((c) => c.id === requestedCity)?.id ||
          cities.find((c) => c.id === preferredCity)?.id ||
          cities[0]?.id ||
          "";
        setSelectedCity(finalCity);

        if (!finalCity || requestRef.current !== requestId) {
          setLeagueOptions([]);
          setSeasonOptions([]);
          setSelectedLeague("");
          setSelectedSeason("");
          return;
        }

        const leaguesRes = await apiGet("/api/meta/leagues", { params: { cityId: finalCity }, cacheTtl: 5 * 60 * 1000 });
        const activeLeagues = safeArr(leaguesRes?.data?.leagues);
        const requestedLeague = toIdString(leagueParam);

        // Arşivden gelinen lig aktif listede olmaz; yalnızca o kaydı ekle ki
        // sayfa varsayılan lige yönlenmesin.
        const leagues = await ensureRequestedScope(activeLeagues, requestedLeague, async () => {
          const res = await apiGet("/api/meta/leagues", {
            params: { cityId: finalCity, includeArchived: 1 },
            cacheTtl: 5 * 60 * 1000,
          });
          return safeArr(res?.data?.leagues);
        });

        const leagueOpts = leagues.map((league) => ({
          id: toIdString(league?.id),
          label: scopeOptionLabel(league),
          raw: league,
        }));
        if (requestRef.current !== requestId) return;
        setLeagueOptions(leagueOpts);

        // Varsayılan her zaman aktif liglerden seçilir.
        const defaultLeagueId = pickDefaultLeagueId(activeLeagues);
        const finalLeague = leagueOpts.find((l) => l.id === requestedLeague)?.id || defaultLeagueId || "";
        setSelectedLeague(finalLeague);

        if (!finalLeague || requestRef.current !== requestId) {
          setSeasonOptions([]);
          setSelectedSeason("");
          if (requestRef.current === requestId) syncPartialPath(finalCity, "", "");
          return;
        }

        const seasonsRes = await apiGet("/api/meta/seasons", { params: { cityId: finalCity, leagueId: finalLeague }, cacheTtl: 5 * 60 * 1000 });
        const activeSeasons = safeArr(seasonsRes?.data?.seasons);
        const requestedSeason = toIdString(seasonParam);

        // Arşivdeki sezon adresi açıksa o sezonu listeye geri koy.
        const seasons = await ensureRequestedScope(activeSeasons, requestedSeason, async () => {
          const res = await apiGet("/api/meta/seasons", {
            params: { cityId: finalCity, leagueId: finalLeague, includeArchived: 1 },
            cacheTtl: 5 * 60 * 1000,
          });
          return safeArr(res?.data?.seasons);
        });

        const seasonOpts = seasons.map((season) => ({
          id: toIdString(season?.id),
          label: scopeOptionLabel(season),
          raw: season,
        }));
        if (requestRef.current !== requestId) return;
        setSeasonOptions(seasonOpts);

        // Arşivlenmiş bir ligin hiç aktif sezonu kalmaz; bu durumda listedeki
        // (arşiv) sezon seçilir ki sayfa boş kalmasın.
        const defaultSeasonId = pickDefaultSeasonId(activeSeasons) || pickDefaultSeasonId(seasons);
        const finalSeason = seasonOpts.find((s) => s.id === requestedSeason)?.id || defaultSeasonId || "";
        setSelectedSeason(finalSeason);

        if (!finalCity || !finalLeague || !finalSeason) {
          syncPartialPath(finalCity, finalLeague, "");
          return;
        }

        const targetPath = targetFor(finalCity, finalLeague, finalSeason);
        const currentPath = pathFor ? `${location.pathname}${location.search}` : location.pathname;
        if (targetPath && currentPath !== targetPath) {
          navigate(targetPath, { replace: true });
        }
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.error || "Veriler alınırken hata oluştu.");
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetFor, cachedCities, cityParam, leagueParam, seasonParam, location.pathname, location.search, navigate, defaultCityId, portal, portalsReady, activePortal]);

  useEffect(() => {
    if (!prefetch || !selectedCity || !selectedLeague || !selectedSeason) return;
    const tid = setTimeout(() => {
      refetchScope?.({ cityId: selectedCity, leagueId: selectedLeague, seasonId: selectedSeason, limit: 300, offset: 0 });
    }, 350);
    return () => clearTimeout(tid);
  }, [prefetch, selectedCity, selectedLeague, selectedSeason, refetchScope]);

  const onCityChange = useCallback(
    (nextCityId) => {
      const next = toIdString(nextCityId);
      if (!next) return;
      // Kendi adres yapısı olan sayfa yerinde kalır; lig/sezon yeni ilde
      // varsayılanlardan tamamlanır.
      navigate(pathFor ? pathFor(next, "", "") : `/sehir/${encodeURIComponent(next)}`, { replace: true });
    },
    [navigate, pathFor]
  );

  const onLeagueChange = useCallback(
    async (nextLeagueId) => {
      if (!selectedCity) return;
      const nextLeague = toIdString(nextLeagueId);
      if (!nextLeague) return;

      if (portal) {
        const league = findPortalLeague(portal, nextLeague);
        const nextSeason =
          nextLeague === toIdString(portal.defaultLeagueId) ? portal.defaultSeasonId : league?.defaultSeasonId;
        const target = portalPath(portal, nextLeague, nextSeason, page);
        if (target) navigate(target, { replace: true });
        return;
      }

      try {
        const seasonsRes = await apiGet("/api/meta/seasons", { params: { cityId: selectedCity, leagueId: nextLeague }, cacheTtl: 5 * 60 * 1000 });
        let seasons = safeArr(seasonsRes?.data?.seasons);

        // Arşivlenmiş bir lig seçildiyse aktif sezon kalmamıştır; arşivdeki
        // sezonlarla devam et ki seçim boşa düşmesin.
        if (!seasons.length) {
          const archivedRes = await apiGet("/api/meta/seasons", {
            params: { cityId: selectedCity, leagueId: nextLeague, includeArchived: 1 },
            cacheTtl: 5 * 60 * 1000,
          });
          seasons = safeArr(archivedRes?.data?.seasons);
        }

        const defaultSeasonId = pickDefaultSeasonId(seasons);
        const targetSeason = defaultSeasonId || toIdString(seasons[0]?.id);
        const targetPath = targetFor(selectedCity, nextLeague, targetSeason);
        if (targetPath) navigate(targetPath, { replace: true });
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.error || "Sezonlar alınırken hata oluştu.");
      }
    },
    [targetFor, navigate, selectedCity, portal, page]
  );

  const onSeasonChange = useCallback(
    (nextSeasonId) => {
      if (!selectedCity || !selectedLeague) return;
      const nextSeason = toIdString(nextSeasonId);
      if (!nextSeason) return;
      const targetPath = targetFor(selectedCity, selectedLeague, nextSeason);
      if (targetPath) navigate(targetPath, { replace: true });
    },
    [targetFor, navigate, selectedCity, selectedLeague]
  );

  return {
    loading,
    error,
    cityOptions,
    leagueOptions,
    seasonOptions,
    selectedCity,
    selectedLeague,
    selectedSeason,
    onCityChange,
    onLeagueChange,
    onSeasonChange,
    scopeLabel,
    hasScopedSelection,
    pagePath,
    portal,
  };
}
